import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { CreditCard, Check, Shield, Calendar, Loader2 } from 'lucide-react';

const API_URL = window.location.origin;

// Initialize Stripe - use test key for now
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_emergent');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#1a3c34',
      fontFamily: '"Manrope", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#9ca3af'
      }
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444'
    }
  }
};

const CheckoutForm = ({ tier, tierInfo, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Get or create SetupIntent
      const token = localStorage.getItem('token');
      const setupResponse = await axios.post(
        `${API_URL}/api/subscription/setup-intent`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!setupResponse.data.client_secret) {
        throw new Error('Failed to initialize payment');
      }

      // Confirm card setup
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        setupResponse.data.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: user?.full_name,
              email: user?.email
            }
          }
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      // Start trial with the payment method
      const trialResponse = await axios.post(
        `${API_URL}/api/subscription/start-trial`,
        {
          tier: tier,
          payment_method_id: setupIntent.payment_method
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Trial started successfully!');
      onSuccess(trialResponse.data);
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || err.message || 'Something went wrong');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Plan Summary */}
      <Card className="bg-forest/5 border-forest/20">
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-heading font-semibold text-forest">{tierInfo?.name}</p>
              <p className="text-sm text-gray-600">30-day free trial</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-forest">{tierInfo?.monthly_price_display}</p>
              <p className="text-sm text-gray-500">per month after trial</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trial Benefits */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Check className="h-4 w-4 text-green-600" />
          <span>Full access for 30 days free</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Check className="h-4 w-4 text-green-600" />
          <span>Cancel anytime during trial</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Check className="h-4 w-4 text-green-600" />
          <span>No charge until trial ends</span>
        </div>
      </div>

      {/* Card Input */}
      <div>
        <label className="block text-sm font-medium text-forest mb-2">
          Card Details
        </label>
        <div className="p-4 border border-sand rounded-xl bg-white">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
          <Shield className="h-3 w-3" />
          Secure payment powered by Stripe
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1"
          disabled={processing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 bg-terracotta hover:bg-terracotta/90 text-white"
        >
          {processing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Calendar className="h-4 w-4 mr-2" />
              Start Free Trial
            </>
          )}
        </Button>
      </div>

      <p className="text-xs text-gray-500 text-center">
        By starting your trial, you agree to our Terms of Service and authorize billing 
        of {tierInfo?.monthly_price_display}/month starting {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}.
      </p>
    </form>
  );
};

const SubscriptionCheckout = ({ isOpen, onClose, tier, tierInfo }) => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const handleSuccess = async (data) => {
    // Refresh user data to get updated tiers
    await refreshUser();
    onClose();
    toast.success(`Welcome to ${tierInfo?.name}! Your 30-day free trial has started.`);
    navigate('/dashboard');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-terracotta" />
            Start Your Free Trial
          </DialogTitle>
          <DialogDescription>
            Add your card to start your 30-day free trial. You won&apos;t be charged today.
          </DialogDescription>
        </DialogHeader>

        <Elements stripe={stripePromise}>
          <CheckoutForm
            tier={tier}
            tierInfo={tierInfo}
            onSuccess={handleSuccess}
            onCancel={onClose}
          />
        </Elements>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionCheckout;
